import { isAbortable } from './abortable-promise';

export type DisposablePromise<T> = Promise<T> & {
	dispose: () => void;
};

/**
 * Adds a dispose function to a promise that calls cancel (if cancellable) and dispose (if disposable).
 * @param promise The promise to add dispose functionality to
 * @param dispose Optional custom dispose function to be called on dispose
 * @returns A disposable promise
 */
export function makeDisposable<T extends Promise<unknown>>(
	promise: T,
	dispose?: () => void,
): DisposablePromise<T> {
	const disposablePromise = promise as unknown as DisposablePromise<T>;
	disposablePromise.dispose = () => {
		// Check if the promise is abortable and call abort
		if (isAbortable(promise)) {
			promise.abort();
		}
		// Call the custom dispose function if provided
		dispose?.();
	};
	return disposablePromise;
}

export function isDisposable<T>(
	promise: Promise<T>,
): promise is Promise<T> & { dispose: () => void } {
	return typeof (promise as any).dispose === 'function';
}
